import { useMemo } from 'react';

import { CadRenderer } from '#components/cad-renderer/cad-renderer.js';
import { ActiveDesignContextProvider } from '#components/cad-renderer/context/active-design-context-provider.js';
import { CadstromColorProviderContextProvider } from '#components/cad-renderer/context/cadstrom-color-provider-context-provider.js';
import { CoreModelContext } from '#components/cad-renderer/context/core-model-context.js';
import {
	InteractionManagerContext,
	useSetUpInteractionManagerContext,
} from '#components/cad-renderer/context/interaction-manager-context.js';

import type { DesignModels, ModelWithNetStats } from '@cadstrom/sigma-engine-shared';
import type { ReactNode } from 'react';
import type { IColorSchemeSet } from '#components/cad-renderer/color-provider/cadstrom-color-provider.js';
import type { CadRendererGridType } from '#components/cad-renderer/sync/bridges/grid-bridge.js';

export type ProjectModelEmbedProps = {
	/** Revision (build) the model belongs to. */
	revisionId: string;
	/** The project model to render. */
	projectModel: ModelWithNetStats;
	/** Design to display. Defaults to the model's first schematic by sheet index. */
	designId?: string;
	/** When set, the initial view zooms to this box instead of the design's own zoom box. */
	bbox?: DesignModels.ModelBoundingBox;
	/** Colour scheme override; defaults to the app scheme. */
	colorScheme?: IColorSchemeSet;
	/** Resolves image asset refs for models that carry image drawings. */
	imageUrlResolver?: (imageRef: string) => string | Promise<string>;
	/**
	 * Rendered in a pointer-transparent layer over the canvas area (excluding
	 * the renderer's toolbar and status bar). Children position themselves with
	 * absolute placement.
	 */
	canvasOverlay?: ReactNode;
	/** Hides the renderer's history navigator and design selector. */
	hideNavigationControls?: boolean;
	/** Hides the renderer's tools column. */
	hideToolbar?: boolean;
	/** Hides the renderer's status bar. */
	hideStatusBar?: boolean;
	/** Grid style used when the design has no saved grid settings yet. Defaults to lines. */
	defaultGridType?: CadRendererGridType;
	className?: string;
};

/**
 * Self-contained embed of one design from a project model: builds the full
 * renderer context stack (core model, colours, active design, interaction) and
 * mounts the CAD renderer, so it can drop into any host — a page, a workbench
 * message, a dialog. Rendering is display-complete but scoped: no design
 * switcher, just the requested design at the requested view.
 *
 * @param props - Component props.
 */
export function ProjectModelEmbed({
	revisionId,
	projectModel,
	designId,
	bbox,
	colorScheme,
	imageUrlResolver,
	canvasOverlay,
	hideNavigationControls,
	hideToolbar,
	hideStatusBar,
	defaultGridType,
	className,
}: ProjectModelEmbedProps) {
	const resolvedDesignId = useMemo(() => {
		if (designId !== undefined && projectModel.designs[designId] !== undefined) {
			return designId;
		}
		return Object.values(projectModel.designs)
			.filter((design) => design.modelType === 'schematic')
			.toSorted((first, second) => first.sheetIndex - second.sheetIndex)[0]?.designId;
	}, [designId, projectModel]);

	// A bounding box is applied as data — the design's initial zoom box — so no
	// imperative renderer calls (or effects) are needed.
	const zoomedModel = useMemo(() => {
		if (bbox === undefined || resolvedDesignId === undefined) {
			return projectModel;
		}
		const design = projectModel.designs[resolvedDesignId];
		if (design === undefined) {
			return projectModel;
		}
		return {
			...projectModel,
			designs: { ...projectModel.designs, [resolvedDesignId]: { ...design, initial_zoombox: bbox } },
		};
	}, [bbox, projectModel, resolvedDesignId]);

	const coreModelContextValue = useMemo(() => ({ coreModel: zoomedModel }), [zoomedModel]);
	const interactionManagerContextValue = useSetUpInteractionManagerContext(zoomedModel);
	const initialSelection = useMemo(
		() => (resolvedDesignId === undefined ? undefined : { designId: resolvedDesignId }),
		[resolvedDesignId],
	);

	return (
		<CoreModelContext value={coreModelContextValue}>
			<CadstromColorProviderContextProvider colorScheme={colorScheme}>
				<ActiveDesignContextProvider
					key={`${resolvedDesignId ?? ''}:${bbox === undefined ? '' : `${bbox.minx},${bbox.miny},${bbox.maxx},${bbox.maxy}`}`}
					initialSelection={initialSelection}
				>
					<InteractionManagerContext value={interactionManagerContextValue}>
						<CadRenderer
							revisionId={revisionId}
							projectModel={zoomedModel}
							imageUrlResolver={imageUrlResolver}
							canvasOverlay={canvasOverlay}
							hideNavigationControls={hideNavigationControls}
							hideToolbar={hideToolbar}
							hideStatusBar={hideStatusBar}
							defaultGridType={defaultGridType}
							className={className}
						/>
					</InteractionManagerContext>
				</ActiveDesignContextProvider>
			</CadstromColorProviderContextProvider>
		</CoreModelContext>
	);
}
